import Styles from './Toolbar.module.scss'
import { Button } from 'blaze-ui/Button/Button'
import { useModals } from 'almanac-ui/Modal'
import type { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import type { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import cx from 'classnames'
import { postNotificationBannerConfigs } from './postNotificationBannerConfig'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'

interface PostNotificationBannerProps {
  scheduledPost: ScheduledPostShow
}

export const PostNotificationBanner = ({ scheduledPost }: PostNotificationBannerProps) => {
  const { openModal } = useModals()
  const { disconnectedIntegrations, hasDisconnectedIntegrations } = useIntegrationHeartbeatStatus()
  const { initiateOauth } = useInitiateOauth()
  const { connect } = useConnectAccountPopup()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const { data: brandKits } = useBrandKits()
  const brandKit = brandKits?.find((brandKit) => !brandKit.incomplete)

  const handleOauthPopup = async (provider: ExternalIntegration['provider'], accountToRefreshId: number) => {
    const popUp = await initiateOauth(provider, accountToRefreshId, '_blank', 'popup=true,width=600,height=780')
    if (popUp && typeof popUp !== 'undefined') {
      connect(popUp as Window, {
        onConnect: () => {
          queryClient.invalidateQueries(calendarKeys.all)
          queryClient.invalidateQueries(['externalIntegrations', workspaceId])
        },
      })
    }
  }

  const additionalData = {
    disconnectedIntegrations,
    hasDisconnectedIntegrations,
    initiateOauth: handleOauthPopup,
    scheduledPost,
    workspaceId,
    brandKit,
  }

  const activePostSettings = postNotificationBannerConfigs.find((postNotificationBanner) =>
    postNotificationBanner.isRendered(scheduledPost, additionalData),
  )

  if (!activePostSettings) {
    return null
  }

  const message =
    typeof activePostSettings.message === 'function'
      ? activePostSettings.message(additionalData)
      : activePostSettings.message

  let secondaryMessage = null
  if (activePostSettings.secondaryMessage) {
    if (typeof activePostSettings.secondaryMessage === 'function') {
      secondaryMessage = activePostSettings.secondaryMessage(additionalData)
    } else {
      secondaryMessage = activePostSettings.secondaryMessage
    }
  }

  const handleActionButtonPress = () => {
    if (activePostSettings.actionButton) {
      activePostSettings.actionButton.onPress({
        scheduledPostId: scheduledPost?.id,
        openModal,
        additionalData,
      })
    }
  }

  const iconColor = activePostSettings.variant === 'warning' ? 'var(--negative-50)' : 'var(--dark-80)'

  return (
    <div
      className={cx(Styles.postSettings, { [Styles.warning]: activePostSettings.variant === 'warning' })}
      data-testid={activePostSettings.testId}
    >
      <div className={Styles.postSettingsTextGroup}>
        {activePostSettings.Icon && <activePostSettings.Icon color={iconColor} size={25} />}
        <div>
          <span className={Styles.postSettingsMessage}>{message}</span>
          {secondaryMessage && <span className={Styles.postSettingsSecondaryMessage}>{secondaryMessage}</span>}
        </div>
      </div>
      {activePostSettings.actionButton && (
        <Button
          size="sm"
          frontIcon={activePostSettings.actionButton.frontIcon}
          endIcon={activePostSettings.actionButton.endIcon}
          className={cx(Styles.actionButton, {
            [Styles.refreshButton]: activePostSettings.variant === 'warning',
          })}
          onPress={handleActionButtonPress}
          variant={activePostSettings.actionButton.variant}
          data-testid={activePostSettings.actionButton.testId}
        >
          {activePostSettings.actionButton.text}
        </Button>
      )}
    </div>
  )
}
